import { datadogRum } from '@datadog/browser-rum';
import { ErrorBoundary } from 'react-error-boundary';

import type { CSSProperties, ReactNode } from 'react';

// Root error boundary fallback. Deliberately vanilla: no i18n, no design-system
// components, no router/urql/context hooks, and inline styles instead of
// Tailwind classes. The whole point of the root net is to stay renderable even
// when the thing that crashed is i18n, the stylesheet, or a provider mounted
// below it — so it cannot depend on any of them. Copy stays hardcoded English.
const containerStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  padding: 24,
  textAlign: 'center',
  // Figtree is the brand font; sans-serif covers us if the stylesheet that
  // loads it is the thing that failed.
  fontFamily: 'Figtree, sans-serif',
  // Own colours so contrast holds regardless of the page theme. light-dark()
  // (driven by colorScheme below) follows the OS preference without depending
  // on the app's theme provider, which may be what crashed.
  colorScheme: 'light dark',
  background: 'light-dark(#fff, #1a1a1a)',
  color: 'light-dark(#1a1a1a, #f5f5f5)',
};

const buttonStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 500,
  color: 'light-dark(#fff, #1a1a1a)',
  background: 'light-dark(#1a1a1a, #f5f5f5)',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
};

const AppErrorFallback = () => {
  return (
    <div role="alert" style={containerStyle}>
      <h1 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Something went wrong</h1>
      <p style={{ fontSize: 14, maxWidth: 420, margin: 0, lineHeight: 1.5 }}>
        The app ran into an unexpected error. Try again, and if the problem persists please{' '}
        {/* light-dark() like the rest of this fallback — a single blue was the
            one value here that didn't adapt, and it sat at ~2.9:1 on the dark
            background. Literals rather than tokens: this net is above the
            providers, so it can't assume the theme layer survived. */}
        <a
          href="https://help.askable.com/en/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'light-dark(#2b4ecc, #8f9cff)' }}
        >
          contact support
        </a>
        .
      </p>
      {/* Full reload, not resetErrorBoundary: this net sits above the providers,
          so a deterministic crash (e.g. a provider throwing on render) would just
          re-throw on reset. A reload is the only reliable recovery here. */}
      <button type="button" onClick={() => window.location.reload()} style={buttonStyle}>
        Try again
      </button>
    </div>
  );
};

export const ErrorBoundaryApp = ({ children }: { children: ReactNode }) => {
  return (
    <ErrorBoundary
      FallbackComponent={AppErrorFallback}
      onError={(error, errorInfo) => {
        datadogRum.addError(error, {
          source: 'app-root',
          type: 'error-boundary',
          errorInfo: errorInfo.componentStack,
        });
        datadogRum.startSessionReplayRecording();
      }}
    >
      {children}
    </ErrorBoundary>
  );
};
